import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import PhotoUpload from '../components/PhotoUpload';
import { equipementsAPI, categoriesAPI } from '../utils/api';

const ETATS = ['neuf', 'bon', 'usagé', 'défectueux'];
const EMPTY = { nom: '', reference: '', categorie: '', marque: '', modele: '', quantite: 0, quantiteMin: 2, emplacement: '', etat: 'neuf', prixUnitaire: 0, fournisseur: '', description: '', imageUrl: '' };
const isMobile = () => window.innerWidth <= 768;

export default function Inventaire() {
  const [equipements, setEquipements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterEtat, setFilterEtat] = useState('');
  const [alerteOnly, setAlerteOnly] = useState(false);
  const [modal, setModal] = useState(null);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [mvForm, setMvForm] = useState({ type: 'entrée', quantite: 1, motif: '', projet: '', client: '', operateur: '' });
  const [saving, setSaving] = useState(false);
  const [mobile, setMobile] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eq, cats] = await Promise.all([
        equipementsAPI.getAll({ search, categorie: filterCat, etat: filterEtat, alerte: alerteOnly || undefined }),
        categoriesAPI.getAll()
      ]);
      setEquipements(eq.data);
      setCategories(cats.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, filterCat, filterEtat, alerteOnly]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setModal('create'); };
  const openEdit = (eq) => {
    setForm({
      ...eq,
      categorie: eq.categorie_id || eq.categorie?._id || eq.categorie || '',
      quantiteMin: eq.quantite_min ?? eq.quantiteMin ?? 2,
      prixUnitaire: eq.prix_unitaire ?? eq.prixUnitaire ?? 0,
    });
    setCurrent(eq);
    setModal('edit');
  };
  const openMouvement = (eq) => {
    setCurrent(eq);
    setMvForm({ type: 'entrée', quantite: 1, motif: '', projet: '', client: '', operateur: '' });
    setModal('mouvement');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === 'create') {
        await equipementsAPI.create(form);
        toast.success('Équipement créé');
      } else {
        await equipementsAPI.update(current.id || current._id, form);
        toast.success('Équipement mis à jour');
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (eq) => {
    if (!window.confirm('Supprimer cet équipement ?')) return;
    try {
      await equipementsAPI.delete(eq.id || eq._id);
      toast.success('Supprimé');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const handleMouvement = async () => {
    setSaving(true);
    try {
      await equipementsAPI.mouvement(current.id || current._id, mvForm);
      toast.success('Mouvement enregistré');
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const qtyClass = (eq) => {
    const min = eq.quantite_min ?? eq.quantiteMin ?? 2;
    if (eq.quantite === 0) return 'qty-low';
    if (eq.quantite <= min) return 'qty-warn';
    return 'qty-ok';
  };

  const getCatName = (eq) => eq.categories?.nom || eq.categorie?.nom || '—';
  const getCatIcon = (eq) => eq.categories?.icone || eq.categorie?.icone || '📦';

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="page-title">Inventaire</div>
          <div className="page-subtitle">{equipements.length} équipement(s) référencé(s)</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Ajouter</button>
      </div>

      <div className="table-container">
        <div className="table-toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="search-wrapper" style={{ minWidth: 180 }}>
            <span className="search-icon">⌕</span>
            <input className="search-input" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select" style={{ width: 'auto', minWidth: 130 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="">Toutes catégories</option>
            {categories.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.nom}</option>)}
          </select>
          {!mobile && (
            <select className="select" style={{ width: 'auto' }} value={filterEtat} onChange={e => setFilterEtat(e.target.value)}>
              <option value="">Tous états</option>
              {ETATS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={alerteOnly} onChange={e => setAlerteOnly(e.target.checked)} />
            Alertes
          </label>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"></div>Chargement...</div>
        ) : equipements.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📦</div>Aucun équipement trouvé</div>
        ) : mobile ? (
          /* MOBILE CARDS */
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {equipements.map(eq => {
              const min = eq.quantite_min ?? eq.quantiteMin ?? 2;
              const isAlert = eq.quantite <= min;
              return (
                <div key={eq.id || eq._id} style={{
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border-light)',
                  borderLeft: isAlert ? '3px solid var(--red)' : '3px solid transparent',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{eq.nom}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{eq.reference}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                      <div className={`qty-num ${qtyClass(eq)}`} style={{ fontSize: 22, fontWeight: 700 }}>{eq.quantite}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>/ {min} min</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    <span className={`badge badge-${eq.etat}`}>{eq.etat}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--border-light)', padding: '2px 8px', borderRadius: 20 }}>
                      {getCatIcon(eq)} {getCatName(eq)}
                    </span>
                    {eq.marque && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{eq.marque}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm btn-green" onClick={() => openMouvement(eq)} style={{ flex: 1, justifyContent: 'center' }}>⇅ Mouvement</button>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(eq)}>✎</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(eq)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* DESKTOP TABLE */
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Référence</th><th>Nom</th><th>Catégorie</th><th>Marque</th>
                  <th>État</th><th>Stock</th><th>Emplacement</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {equipements.map(eq => {
                  const min = eq.quantite_min ?? eq.quantiteMin ?? 2;
                  return (
                    <tr key={eq.id || eq._id} style={eq.quantite <= min ? { borderLeft: '3px solid var(--red)' } : {}}>
                      <td><span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{eq.reference}</span></td>
                      <td style={{ fontWeight: 600 }}>{eq.nom}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{getCatIcon(eq)} {getCatName(eq)}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{eq.marque} {eq.modele}</td>
                      <td><span className={`badge badge-${eq.etat}`}>{eq.etat}</span></td>
                      <td>
                        <div className="qty-bar">
                          <span className={`qty-num ${qtyClass(eq)}`}>{eq.quantite}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>/ {min}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{eq.emplacement || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm btn-green" onClick={() => openMouvement(eq)}>⇅</button>
                          <button className="btn btn-sm btn-outline" onClick={() => openEdit(eq)}>✎</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(eq)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL CREATE/EDIT */}
      {(modal === 'create' || modal === 'edit') && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Nouvel équipement' : 'Modifier équipement'}</span>
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Nom *</label>
                  <input className="input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Ex: FortiGate 60F" />
                </div>
                <div className="form-group">
                  <label className="form-label">Référence *</label>
                  <input className="input" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Ex: FG-60F-001" />
                </div>
                <div className="form-group">
                  <label className="form-label">Catégorie *</label>
                  <select className="select" value={form.categorie} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                    <option value="">— Sélectionner —</option>
                    {categories.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.nom}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">État</label>
                  <select className="select" value={form.etat} onChange={e => setForm(f => ({ ...f, etat: e.target.value }))}>
                    {ETATS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Marque</label>
                  <input className="input" value={form.marque || ''} onChange={e => setForm(f => ({ ...f, marque: e.target.value }))} placeholder="Ex: Fortinet" />
                </div>
                <div className="form-group">
                  <label className="form-label">Modèle</label>
                  <input className="input" value={form.modele || ''} onChange={e => setForm(f => ({ ...f, modele: e.target.value }))} placeholder="Ex: 60F" />
                </div>
                <div className="form-group">
                  <label className="form-label">Quantité</label>
                  <input className="input" type="number" min="0" value={form.quantite} onChange={e => setForm(f => ({ ...f, quantite: Number(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Seuil minimal</label>
                  <input className="input" type="number" min="0" value={form.quantiteMin} onChange={e => setForm(f => ({ ...f, quantiteMin: Number(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Emplacement</label>
                  <input className="input" value={form.emplacement || ''} onChange={e => setForm(f => ({ ...f, emplacement: e.target.value }))} placeholder="Ex: Armoire A - Étagère 2" />
                </div>
                <div className="form-group">
                  <label className="form-label">Fournisseur</label>
                  <input className="input" value={form.fournisseur || ''} onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))} placeholder="Ex: Ingram Micro" />
                </div>
                <div className="form-group">
                  <label className="form-label">Prix unitaire (FCFA)</label>
                  <input className="input" type="number" min="0" value={form.prixUnitaire || 0} onChange={e => setForm(f => ({ ...f, prixUnitaire: Number(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date d'achat</label>
                  <input className="input" type="date" value={form.dateAchat ? String(form.dateAchat).split('T')[0] : ''} onChange={e => setForm(f => ({ ...f, dateAchat: e.target.value }))} />
                </div>
                <div className="form-group full">
                  <label className="form-label">Photo</label>
                  <PhotoUpload
                    currentUrl={form.imageUrl}
                    equipementId={current?.id || current?._id}
                    onUpload={(url) => setForm(f => ({ ...f, imageUrl: url }))}
                  />
                </div>
                                <div className="form-group full">
                  <label className="form-label">Description</label>
                  <textarea className="textarea" rows={3} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Notes, caractéristiques..." />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MOUVEMENT */}
      {modal === 'mouvement' && current && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">Mouvement de stock</span>
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'var(--border-light)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', marginBottom: 4 }}>{current.reference}</div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{current.nom}</div>
                <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Stock actuel :</span>
                  <span style={{ fontWeight: 700, fontSize: 20, color: current.quantite <= (current.quantite_min ?? 2) ? 'var(--red)' : 'var(--green)' }}>{current.quantite}</span>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Type de mouvement</label>
                  <select className="select" value={mvForm.type} onChange={e => setMvForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="entrée">Entrée (réception)</option>
                    <option value="sortie">Sortie (déploiement)</option>
                    <option value="ajustement">Ajustement</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantité</label>
                  <input className="input" type="number" min="1" value={mvForm.quantite} onChange={e => setMvForm(f => ({ ...f, quantite: Number(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Projet / Client</label>
                  <input className="input" value={mvForm.projet} onChange={e => setMvForm(f => ({ ...f, projet: e.target.value }))} placeholder="Ex: T&P - Makepe" />
                </div>
                <div className="form-group">
                  <label className="form-label">Opérateur</label>
                  <input className="input" value={mvForm.operateur} onChange={e => setMvForm(f => ({ ...f, operateur: e.target.value }))} placeholder="Ex: Hermann" />
                </div>
                <div className="form-group full">
                  <label className="form-label">Motif</label>
                  <input className="input" value={mvForm.motif} onChange={e => setMvForm(f => ({ ...f, motif: e.target.value }))} placeholder="Ex: Déploiement site Bonanjo" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Annuler</button>
              <button className={`btn ${mvForm.type === 'entrée' ? 'btn-green' : mvForm.type === 'sortie' ? 'btn-danger' : 'btn-amber'}`}
                onClick={handleMouvement} disabled={saving}>
                {saving ? '...' : `Confirmer ${mvForm.type}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
